import { monthName, monthStarted } from "./format";
import { ZOOM_STEPS } from "./zoom";
import styles from "./accounting.module.css";

/** The year, as a segmented control — every year that holds money, and this one. */
export function YearPicker({
  years,
  value,
  onChange,
  testId,
}: {
  years: readonly number[];
  value: number;
  onChange: (year: number) => void;
  testId: string;
}): React.JSX.Element {
  return (
    <div className={styles.segmented} role="group" aria-label="Year" data-testid={testId}>
      {years.map((year) => (
        <button
          key={year}
          type="button"
          className={year === value ? `${styles.segment} ${styles.segmentOn}` : styles.segment}
          aria-pressed={year === value}
          onClick={() => onChange(year)}
          data-testid={`${testId}-${String(year)}`}
        >
          {year}
        </button>
      ))}
    </div>
  );
}

/**
 * "Whole year", or one month of it. **Only months that have begun are
 * offered** — a month still ahead has nothing in it to show.
 *
 * `value` 0 is the whole year.
 */
export function MonthSelect({
  year,
  today,
  value,
  onChange,
  label,
  testId,
}: {
  year: number;
  today: string;
  value: number;
  onChange: (month: number) => void;
  label: string;
  testId: string;
}): React.JSX.Element {
  const months = Array.from({ length: 12 }, (_, i) => i + 1).filter((month) =>
    monthStarted(year, month, today),
  );
  return (
    <select
      className={styles.select}
      aria-label={label}
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
      data-testid={testId}
    >
      <option value={0}>Whole year</option>
      {months.map((month) => (
        <option key={month} value={month}>
          {monthName(month)}
        </option>
      ))}
    </select>
  );
}

/**
 * How large the screen is drawn (STORY-186). A plain choice of sizes, remembered
 * in this browser by the page; it never touches the header it sits in.
 */
export function ZoomSelect({
  value,
  onChange,
}: {
  value: number;
  onChange: (zoom: number) => void;
}): React.JSX.Element {
  return (
    <label className={styles.zoomControl}>
      <span className={styles.locationsLabel}>Zoom</span>
      <select
        className={styles.select}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        aria-label="Zoom"
        data-testid="accounting-zoom"
      >
        {ZOOM_STEPS.map((step) => (
          <option key={step} value={step}>
            {step}%
          </option>
        ))}
      </select>
    </label>
  );
}

/** Adds a year to a list of years if it is missing, oldest first. */
export function withYear(years: readonly number[], year: number): number[] {
  return [...new Set([...years, year])].sort((a, b) => a - b);
}
